import React, { FC, useCallback, useEffect, useState } from 'react'
import styles from 'styles/components/accordion.module.scss'
import { SmallHeadline, SubDescription } from 'components/Typography'
import { Spacer } from 'components/Utility'
import { BusinessHourTable, HolidaysLabel, StoreCalendarsLabel } from 'components/Card'
import { FlexRow } from 'components/Flex'
import { TextButton } from 'components/Button'
import { useDispatch, useSelector } from 'react-redux'
import { State } from 'index'
import { getRole, getStoreRef } from 'reducks/auth/selectors'
import { getHolidays } from 'reducks/store/selectors'
import { getStoreCalendars } from 'reducks/storeCalendar/selectors'
import { Role } from 'lib/role'
import { useToggleBoolean } from 'hooks/form'
import { useCalendarsWithinRange } from 'hooks/storeCalendar'
import startOfMonth from 'date-fns/startOfMonth'
import endOfMonth from 'date-fns/endOfMonth'
import addMonths from 'date-fns/addMonths'
import { fetchStoreCalendars } from 'reducks/storeCalendar/operations'
import { openModal } from 'reducks/modal/operations'
import { OpenCalendarModal, CloseCalendarModal } from 'components/Modal'

const BusinessHourAccordion: FC = () => {
  const dispatch = useDispatch()
  const role = useSelector((state: State) => getRole(state))
  const storeRef = useSelector((state: State) => getStoreRef(state))
  const holidays = useSelector((state: State) => getHolidays(state))
  const storeCalendars = useSelector((state: State) => getStoreCalendars(state))
  const roleManager = new Role(role, storeRef)

  const [isExpand, setExpand] = useState<boolean>(true)
  const toggle = useToggleBoolean(setExpand)

  // 当月1日から翌月末までの臨時休業日を取得
  const temporaryCloseCalendars = useCalendarsWithinRange(
    storeCalendars,
    'close',
    startOfMonth(new Date()),
    endOfMonth(addMonths(new Date(), 1))
  )
  const temporaryOpenCalendars = useCalendarsWithinRange(
    storeCalendars,
    'open',
    startOfMonth(new Date()),
    endOfMonth(addMonths(new Date(), 1))
  )

  // ストア情報と臨時休業日/営業日を取得
  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchStoreCalendars(storeRef, startOfMonth(new Date())))
  }, [storeRef])

  const openCloseCalendarModal = useCallback(() => {
    dispatch(
      openModal(
        <CloseCalendarModal />,
        '臨時休業日の設定',
        '商品の注文を受け付けない日にちを選択して保存してください\n商品の注文を受け付ける場合は、臨時休業日を再度選択してください'
      )
    )
  }, [dispatch])

  const openOpenCalendarModal = useCallback(() => {
    dispatch(
      openModal(
        <OpenCalendarModal />,
        '臨時営業日の設定',
        '臨時営業日や、通常と異なる営業時間を設定する場合は、変更したい日にちをタップしてください'
      )
    )
  }, [dispatch])

  return (
    <section className={isExpand ? styles.open : ''}>
      <FlexRow justifyContent={'between'}>
        <SmallHeadline label={'営業時間'} iconPath={'/icon/grey/clock.svg'} />
        <FlexRow justifyContent={'end'}>
          <div className={styles.businessHour__arrow__wrapper}>
            <span className={styles.businessHour__arrow} />
          </div>
          <TextButton label={isExpand ? '詳細を閉じる' : '詳細を開く'} onClick={toggle} />
        </FlexRow>
      </FlexRow>
      <div className={styles.businessHour__item}>
        <Spacer size={12} />
        <BusinessHourTable />
        <Spacer size={16} />
        <SubDescription
          description={
            '当日取り置き商品については、オーダー開始時刻からラストオーダー時刻の間で、お客さまからの注文を受け付けます。'
          }
        />
        <Spacer size={16} />
        <SmallHeadline label={'定休日(毎週)'} iconPath={'/icon/grey/holiday.svg'} />
        <Spacer size={12} />
        <HolidaysLabel holidays={holidays} />
        <Spacer size={16} />
        <FlexRow justifyContent={'between'}>
          <SmallHeadline label={'臨時休業日(翌月まで表示)'} iconPath={'/icon/grey/holiday.svg'} />
          {roleManager.hasEnoughRole(9) && (
            <TextButton label={'臨時休業日を設定'} onClick={openCloseCalendarModal} size={'small'} />
          )}
        </FlexRow>
        <Spacer size={12} />
        <StoreCalendarsLabel storeCalendars={temporaryCloseCalendars} />
        <Spacer size={16} />
        <FlexRow justifyContent={'between'}>
          <SmallHeadline label={'臨時営業日(翌月まで表示)'} iconPath={'/icon/grey/open.svg'} />
          {roleManager.hasEnoughRole(9) && (
            <TextButton label={'臨時営業日を設定'} onClick={openOpenCalendarModal} size={'small'} />
          )}
        </FlexRow>
        <Spacer size={12} />
        <StoreCalendarsLabel storeCalendars={temporaryOpenCalendars} />
      </div>
    </section>
  )
}

export default BusinessHourAccordion
